import React, { useMemo } from 'react';
import { Users, Euro, Home, TrendingUp, Calendar, AlertCircle, Clock, CheckCircle2, Mail, Phone, MessageCircle, MessageSquare, Zap } from 'lucide-react';
import { Deal, Owner, DealStage, Property, Agency, UserProfile } from '../types';

interface DashboardProps {
    deals?: Deal[];
    owners?: Owner[];
    properties?: Property[];
    agencies?: Agency[];
    userProfile?: UserProfile | null;
    onUpdateDeal?: (deal: Deal) => void;
    onDeleteDeal?: (id: string) => void;
    onNavigateToDeal?: (dealId: string, tab: 'notes' | 'reminders' | 'sequence') => void;
}

export const Dashboard: React.FC<DashboardProps> = ({ 
    deals = [], owners = [], properties = [], agencies = [], userProfile = null, onUpdateDeal, onDeleteDeal, onNavigateToDeal
}) => {
  
  // STATS CALCULATIONS
  const stats = useMemo(() => {
      // 1. Pipe Actif (tout ce qui n'est ni gagné ni perdu)
      const activeDeals = deals.filter(d => d.stage !== DealStage.LOST && d.stage !== DealStage.SIGNED);
      
      // 2. CA Estimé (Mensuel) : Somme des valeurs des deals actifs
      const totalRevenue = activeDeals.reduce((acc, curr) => acc + (curr.value || 0), 0);

      // 3. Taux de Conversion : Gagnés / (Gagnés + Perdus)
      const wonCount = deals.filter(d => d.stage === DealStage.SIGNED).length;
      const lostCount = deals.filter(d => d.stage === DealStage.LOST).length;
      const closedCount = wonCount + lostCount;
      const conversionRate = closedCount > 0 ? Math.round((wonCount / closedCount) * 100) : 0;

      return {
          ownersCount: owners.length,
          propertiesCount: 0, 
          pipelineCount: activeDeals.length,
          estimatedRevenue: totalRevenue,
          conversionRate: conversionRate
      };
  }, [deals, owners]);

  // TASKS LIST (Reminders + Sequence Steps)
  const tasks = useMemo(() => {
      const allItems = deals.flatMap(deal => {
          const dealOwner = owners.find(o => o.id === deal.ownerId);
          const ownerName = dealOwner ? `${dealOwner.firstName} ${dealOwner.lastName}` : 'Inconnu';
          
          // 1. Manual Reminders
          const reminders = (deal.reminders || [])
            .filter(r => !r.isDone)
            .map(r => ({
                id: r.id,
                date: r.date,
                note: r.note,
                type: 'REMINDER',
                subType: null,
                dealId: deal.id,
                ownerName,
                dealStage: deal.stage,
                dateObj: new Date(r.date)
            }));

          // 2. Sequence Steps (Pending)
          const steps = (deal.sequenceSteps || [])
            .filter(s => s.status === 'PENDING')
            .map(s => ({
                id: s.id,
                date: s.date,
                note: s.title,
                type: 'SEQUENCE',
                subType: s.type, // EMAIL, CALL, WHATSAPP...
                dealId: deal.id,
                ownerName,
                dealStage: deal.stage,
                dateObj: new Date(s.date)
            }));

          return [...reminders, ...steps];
      });

      // Sort: Overdue & Today first, then future
      return allItems.sort((a, b) => a.dateObj.getTime() - b.dateObj.getTime());
  }, [deals, owners]);

  const today = new Date();
  today.setHours(0,0,0,0);

  const getTaskIcon = (type: string, subType: string | null, isLate: boolean, isToday: boolean) => {
      const colorClass = isLate ? 'text-red-500' : isToday ? 'text-amber-500' : type === 'SEQUENCE' ? 'text-purple-500' : 'text-green-500';
      
      if (type === 'REMINDER') {
          return isLate ? <AlertCircle className={`w-5 h-5 ${colorClass}`} /> : <Clock className={`w-5 h-5 ${colorClass}`} />;
      }
      
      // Sequence Icons
      switch(subType) {
          case 'EMAIL': return <Mail className={`w-5 h-5 ${colorClass}`} />;
          case 'CALL': return <Phone className={`w-5 h-5 ${colorClass}`} />;
          case 'WHATSAPP': return <MessageCircle className={`w-5 h-5 ${colorClass}`} />;
          case 'SMS': return <MessageSquare className={`w-5 h-5 ${colorClass}`} />;
          default: return <Zap className={`w-5 h-5 ${colorClass}`} />;
      }
  };

  const handleTaskClick = (dealId: string, type: string) => {
      if (onNavigateToDeal) {
          onNavigateToDeal(dealId, type === 'SEQUENCE' ? 'sequence' : 'reminders');
      }
  };

  return (
    <div className="p-8 h-full overflow-y-auto">
      <h2 className="text-2xl font-bold text-slate-900 mb-6">Tableau de Bord</h2>
      
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 flex items-center">
            <div className="p-3 bg-blue-50 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
                <p className="text-sm text-slate-500 font-medium">Contacts</p>
                <p className="text-2xl font-bold text-slate-800">{stats.ownersCount}</p>
            </div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 flex items-center">
            <div className="p-3 bg-emerald-50 rounded-lg">
                <Home className="w-6 h-6 text-emerald-600" />
            </div>
            <div className="ml-4">
                <p className="text-sm text-slate-500 font-medium">Pipe Actif</p>
                <p className="text-2xl font-bold text-slate-800">{stats.pipelineCount}</p>
            </div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 flex items-center">
            <div className="p-3 bg-amber-50 rounded-lg">
                <TrendingUp className="w-6 h-6 text-amber-600" />
            </div>
            <div className="ml-4">
                <p className="text-sm text-slate-500 font-medium">Taux Conversion</p>
                <p className="text-2xl font-bold text-slate-800">{stats.conversionRate} %</p>
            </div>
        </div>
         <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 flex items-center">
            <div className="p-3 bg-purple-50 rounded-lg">
                <Euro className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
                <p className="text-sm text-slate-500 font-medium">CA Estimé (Mensuel)</p>
                <p className="text-2xl font-bold text-slate-800">{stats.estimatedRevenue.toLocaleString()} €</p>
            </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* TASKS LIST */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col h-[500px]">
            <div className="p-4 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
                <h3 className="font-bold text-slate-800 flex items-center">
                    <CheckCircle2 className="w-5 h-5 mr-2 text-amber-500" />
                    Tâches & Séquences ({tasks.length})
                </h3>
            </div>
            <div className="flex-1 overflow-y-auto p-0">
                {tasks.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-slate-400">
                        <CheckCircle2 className="w-12 h-12 mb-2 opacity-20" />
                        <p>Aucune tâche prévue. Profitez-en !</p>
                    </div>
                ) : (
                    <div className="divide-y divide-slate-100">
                        {tasks.map((task, i) => {
                            const taskDate = new Date(task.date);
                            taskDate.setHours(0,0,0,0);
                            const isLate = taskDate < today;
                            const isToday = taskDate.getTime() === today.getTime();
                            
                            // Visual distinction for Sequences vs Reminders
                            const borderClass = isLate 
                                ? 'border-l-red-500 bg-red-50/30' 
                                : isToday 
                                    ? 'border-l-amber-500' 
                                    : task.type === 'SEQUENCE' 
                                        ? 'border-l-purple-500' 
                                        : 'border-l-green-500';

                            return (
                                <div key={i} onClick={() => task.dealId && handleTaskClick(task.dealId, task.type)} className={`p-4 hover:bg-slate-50 transition-colors flex items-start border-l-4 cursor-pointer ${borderClass}`}>
                                    <div className="mr-3 mt-1 shrink-0">
                                        {getTaskIcon(task.type, task.subType, isLate, isToday)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-start mb-1">
                                            <p className={`font-semibold text-sm truncate pr-2 ${isLate ? 'text-red-700' : 'text-slate-800'}`}>
                                                {task.type === 'SEQUENCE' && <span className="text-[10px] font-normal uppercase text-purple-600 mr-2 border border-purple-200 px-1 rounded">Auto</span>}
                                                {task.note}
                                            </p>
                                            <span className={`text-xs font-bold px-2 py-0.5 rounded whitespace-nowrap ${isLate ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-500'}`}>
                                                {new Date(task.date).toLocaleDateString(undefined, {day: 'numeric', month:'short'})}
                                            </span>
                                        </div>
                                        <div className="flex items-center text-xs text-slate-500">
                                            <span className="font-medium mr-2 truncate">{task.ownerName}</span>
                                            <span className="bg-slate-100 px-1.5 rounded text-[10px] uppercase border border-slate-200 truncate">{task.dealStage}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>

        {/* OCCUPATION CHART PLACEHOLDER */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 h-[500px] flex flex-col">
            <div className="flex justify-between items-center mb-6">
                <h3 className="font-bold text-slate-800 flex items-center">
                    <Calendar className="w-5 h-5 mr-2 text-blue-500" />
                    Taux d'occupation
                </h3>
            </div>
            <div className="flex-1 flex flex-col justify-center items-center text-slate-300 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
                <div className="h-40 w-full max-w-xs bg-slate-200 rounded animate-pulse mb-4"></div>
                <p className="font-medium">Données en cours de synchronisation...</p>
                <p className="text-xs mt-2">Nécessite une connexion Channel Manager</p>
            </div>
        </div>
      </div>
    </div>
  );
};